import { useUpdateDataScienceTaskMutation } from 'app/apis/commandApi';
import {
  useGetDataScienceTaskDefaultParametersQuery,
  useGetDataScienceTasksQuery
} from 'app/apis/queryApi';
import { RootState } from 'app/store';
import Button from 'common/components/buttons/button';
import { ButtonTheme } from 'common/components/buttons/button-theme';
import Textbox from 'common/components/inputs/textbox';
import Spinner from 'common/components/loading/spinner';
import { DataScienceFeatures } from 'features/data-science/task-features';
import {
  DataScienceTask,
  PipelineParameterOverrides
} from 'features/data-science/tasks.models';
import TileGridContainer from 'features/tiles/tileGridContainer';
import TileWithoutDefaultStyling from 'features/tiles/tileWithoutDefaultStyling';
import { onSuccess } from 'features/toast/toast.slice';
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
const dataScienceFeatureTitles: Record<DataScienceFeatures, string> = {
  [DataScienceFeatures.DDA]: 'DDA',
  [DataScienceFeatures.HybridImpressions]: 'DDA & Impressions'
};

const ReadonlyParameterNames = [
  'siteId',
  'machineLearningModel',
  'lastProcessed',
  'lastTriggered',
  'hasAvailableModel',
  'dataScienceTaskFailureInformation',
  'schedule'
];

const SettingsEditDataScienceTaskPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { siteId } = useSelector((state: RootState) => state.selectedSite);

  const { targetDataScienceFeature } = useParams();

  const [
    newlySetDataScienceTaskOverrideParameters,
    setDataScienceTaskOverrideParameters
  ] = useState<PipelineParameterOverrides[] | null>(null);

  const [newlySetDataScienceTaskParameters, setDataScienceTaskParameters] =
    useState<Record<string, string> | null>(null);

  const setDataScienceTaskOverrideParameter = (key: string, value: string) => {
    setDataScienceTaskOverrideParameters((prev) =>
      prev
        ? prev
            .map((param) => (param.name === key ? { ...param, value } : param))
            .concat(
              prev.some((param) => param.name === key)
                ? []
                : [{ name: key, value }]
            )
        : [{ name: key, value }]
    );
  };

  const setDataScienceTaskParameter = (key: string, value: string) => {
    setDataScienceTaskParameters((prev) => ({
      ...(prev ?? {}),
      [key]: value
    }));
  };

  const isParameterReadonly = (key: string) => {
    return ReadonlyParameterNames.includes(key);
  };

  const {
    data: dataScienceTaskDefaultParametersData,
    isFetching: isFetchingDefaultParameters
  } = useGetDataScienceTaskDefaultParametersQuery(
    {
      siteId,
      dataScienceFeature: targetDataScienceFeature as DataScienceFeatures
    },
    {
      skip: targetDataScienceFeature == null
    }
  );

  const defaultModelTrainingPipelineParameters = useMemo(() => {
    if (dataScienceTaskDefaultParametersData?.modelTrainingPipelineParameters) {
      return Object.entries(
        dataScienceTaskDefaultParametersData.modelTrainingPipelineParameters
      ).filter(([key, _]) => key !== 'SiteId');
    }
    return [];
  }, [dataScienceTaskDefaultParametersData]);

  const { data: dataScienceTasksData, isFetching: isFetchingDataScienceTasks } =
    useGetDataScienceTasksQuery({
      siteId
    });

  const dataScienceTask = useMemo(() => {
    if (!isFetchingDataScienceTasks && dataScienceTasksData) {
      const dataScienceTask = dataScienceTasksData.find(
        (task) => task.machineLearningModel === targetDataScienceFeature
      );

      if (dataScienceTask) {
        return dataScienceTask;
      }
    }
    return {} as DataScienceTask;
  }, [
    dataScienceTasksData,
    isFetchingDataScienceTasks,
    targetDataScienceFeature
  ]);

  const dataScienceTaskParameters = useMemo(() => {
    if (dataScienceTask) {
      return Object.entries(dataScienceTask).filter(
        ([key, _]) =>
          key !== 'pipelineParameterOverrides' &&
          key !== 'siteId' &&
          key !== 'machineLearningModel'
      );
    }
    return [];
  }, [dataScienceTask]);

  const dataScienceTaskOverrideViewableParameters = useMemo(() => {
    if (dataScienceTask.pipelineParameterOverrides) {
      return dataScienceTask.pipelineParameterOverrides.filter(
        (param) => param.name !== 'SiteId'
      );
    }
    return [];
  }, [dataScienceTask]);

  const [updateDataScienceTask, { isLoading, isError, error }] =
    useUpdateDataScienceTaskMutation();

  const onUpdateDataScienceTask = async () => {
    const mergedParameters = dataScienceTask.pipelineParameterOverrides.map(
      (param) => {
        const override = newlySetDataScienceTaskOverrideParameters?.find(
          (newParam) => newParam.name === param.name
        );
        return override ? { ...param, value: override.value } : param;
      }
    );

    const additionalParameters =
      newlySetDataScienceTaskOverrideParameters?.filter(
        (newParam) =>
          !dataScienceTask.pipelineParameterOverrides.some(
            (param) => param.name === newParam.name
          )
      ) ?? [];

    const updatedDataScienceTaskParameters: DataScienceTask = {
      ...dataScienceTask,
      ...Object.fromEntries(
        Object.entries(dataScienceTask ?? {}).map(([key, value]) => {
          if (key !== 'pipelineParameterOverrides') {
            const override = newlySetDataScienceTaskParameters?.[key];
            return [key, override ?? value];
          }
          return [key, value];
        })
      )
    };

    try {
      await updateDataScienceTask({
        siteId,
        dataScienceTask: {
          ...updatedDataScienceTaskParameters,
          pipelineParameterOverrides: [
            ...mergedParameters,
            ...additionalParameters
          ]
        } as DataScienceTask
      }).unwrap();

      dispatch(
        onSuccess({
          title: 'Data Science Task Updated',
          description: `The ${targetDataScienceFeature} task has successfully been updated!`
        })
      );

      navigate('/settings/data-science/tasks');
    } catch (error) {
      console.error('Failed to edit data science task:', error);
    }
  };

  return (
    <TileGridContainer>
      <TileWithoutDefaultStyling className="bg-white border rounded border-neutral-200 p-2.5 shadow-sm w-full grow basis-full">
        <section className="mx-6 my-6">
          <h2 className="text-primary-700 text-l leading-7 pb-5 mb-3 mt-2 border-b border-neutral-200 ">
            Edit Data Science Task
          </h2>
          {targetDataScienceFeature != null ? (
            <>
              <p className="mt-4">
                You have selected the{' '}
                {
                  dataScienceFeatureTitles[
                    targetDataScienceFeature as DataScienceFeatures
                  ]
                }{' '}
                feature.
              </p>
              <br />
              <p className="mt-4">
                Please fill out the form below to edit the data science task:
              </p>
              <br />
              {isError && error && (
                <>
                  {Object.entries(
                    (
                      (error as FetchBaseQueryError).data as {
                        Title: string;
                        Errors: { [propName: string]: string[] };
                      }
                    )?.Errors
                  ).map(([key, value], index) => {
                    return (
                      <div
                        key={`${key}_error_container_${index}`}
                        className="mt-4"
                      >
                        <p
                          key={`${key}_error_title_${index}`}
                          className="text-sm text-error-300 mt-2"
                        >
                          {`Error Property Name: ${key}`}
                        </p>
                        <p
                          key={`${key}_error_message_${index}`}
                          className="text-sm text-error-300 mt-2"
                        >
                          {value}
                        </p>
                      </div>
                    );
                  })}
                </>
              )}
              {!isFetchingDataScienceTasks &&
              !isFetchingDefaultParameters &&
              defaultModelTrainingPipelineParameters &&
              defaultModelTrainingPipelineParameters.length > 0 ? (
                <>
                  {dataScienceTaskParameters.length > 0 && (
                    <>
                      <p className="mt-4">
                        The following parameters are currently set:
                      </p>
                      {dataScienceTaskParameters.map(([key, value], index) => {
                        return (
                          <div
                            key={`${key}_main_parameter_input_container_${index}`}
                            className="mt-4"
                          >
                            <label htmlFor={key} className="block mb-1 text-sm">
                              {key
                                .replace(/([a-z])([A-Z])/g, '$1 $2')
                                .replace(/_/g, ' ')
                                .replace(/^\w/, (c) => c.toUpperCase())}
                              :
                            </label>
                            <Textbox
                              errors={[]}
                              identifier={`${key}_main_parameter_textbox_${index}`}
                              disabled={isParameterReadonly(key)}
                              value={
                                newlySetDataScienceTaskParameters?.[key] ??
                                value ??
                                ''
                              }
                              placeholder="No default value"
                              onChange={(newValue) => {
                                setDataScienceTaskParameter(key, newValue);
                              }}
                            />
                          </div>
                        );
                      })}
                    </>
                  )}
                  {defaultModelTrainingPipelineParameters.map(
                    ([key, value], index) => {
                      return (
                        <div
                          key={`${key}_parameter_override_input_container_${index}`}
                          className="mt-4"
                        >
                          <label htmlFor={key} className="block mb-1 text-sm">
                            {key
                              .replace(/([a-z])([A-Z])/g, '$1 $2')
                              .replace(/_/g, ' ')}
                            :
                          </label>
                          <Textbox
                            errors={[]}
                            identifier={`${key}_parameter_override_textbox_${index}`}
                            value={
                              newlySetDataScienceTaskOverrideParameters?.find(
                                (parameter) => parameter.name === key
                              )?.value ??
                              dataScienceTaskOverrideViewableParameters?.find(
                                (parameter) => parameter.name === key
                              )?.value ??
                              ''
                            }
                            placeholder={
                              value === undefined ? 'No default value' : value
                            }
                            onChange={(value) => {
                              setDataScienceTaskOverrideParameter(key, value);
                            }}
                          />
                        </div>
                      );
                    }
                  )}
                  <br />
                  <Button
                    onClick={async () => await onUpdateDataScienceTask()}
                    theme={ButtonTheme.CREATE}
                    identifier="edit-data-science-task"
                    isLoading={isLoading}
                  >
                    <p>Edit</p>
                  </Button>
                </>
              ) : (
                <>
                  {isFetchingDataScienceTasks ? (
                    <>
                      <br />
                      <div className="flex justify-center">
                        <Spinner />
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-neutral-500">
                      No controllable parameters available for this feature.
                    </p>
                  )}
                </>
              )}
            </>
          ) : (
            <p className="text-sm text-neutral-500">
              No data science task could be found.
            </p>
          )}
        </section>
      </TileWithoutDefaultStyling>
    </TileGridContainer>
  );
};

export default SettingsEditDataScienceTaskPage;
